import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  [
    "inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap",
    "font-medium tracking-[-0.005em]",
    "outline-none transition-[background-color,color,border-color,transform,opacity]",
    "duration-150 ease-[var(--ease-out-quart)]",
    "active:scale-[0.98]",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-3.5",
    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink",
  ].join(" "),
  {
    variants: {
      variant: {
        default:
          "bg-ink text-paper hover:bg-ink/90 rounded-md",
        accent:
          "bg-accent text-paper hover:bg-accent-2 rounded-md",
        outline:
          "border border-line bg-transparent text-ink hover:border-ink-3 hover:bg-paper-2 rounded-md",
        secondary:
          "bg-paper-2 text-ink hover:bg-paper-3 rounded-md",
        ghost:
          "text-ink-2 hover:text-ink hover:bg-paper-2 rounded-md",
        link:
          "text-ink underline underline-offset-[3px] decoration-line-strong hover:decoration-ink decoration-1 rounded-none",
        destructive:
          "bg-destructive text-paper hover:bg-destructive/90 rounded-md",
      },
      size: {
        default: "h-9 px-4 text-sm",
        xs: "h-6 px-2 text-xs gap-1 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 px-3 text-xs gap-1.5",
        lg: "h-11 px-6 text-sm",
        icon: "size-9",
        "icon-sm": "size-8",
        "icon-xs": "size-6 [&_svg:not([class*='size-'])]:size-3",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot.Root : "button";

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
